/**
 * The league fee on the overview (payments phase 2c): what it costs, my
 * status and a Pay button, and for the host who has paid. Also closes a
 * cancelled Stripe Checkout when the payer comes back to the league.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../services/api/apiService';
import paymentService from '../../services/api/paymentService';
import { RefundPolicyNote, HostPaymentsLink } from '../payments/RefundPolicy';
import PaidSummary from '../payments/PaidSummary';
import { formatMoney } from '../../utils/money';
import { formatDateOnly } from '../../utils/dateOnly';

const STATUS_TEXT = { paid: 'Paid', refunded: 'Refunded', pending: 'Unpaid', unpaid: 'Unpaid' };
const statusColor = (s) => (s === 'paid' ? '#7dd3fc' : s === 'refunded' ? '#94a3b8' : '#fcd34d');
const night = (d) => formatDateOnly(d, { weekday: 'short', month: 'short', day: 'numeric' }, d);

/** Per-night fee: one tap reminds everyone who hasn't paid the next game night. */
function RemindNextNight({ leagueId }) {
  const [state, setState] = useState(null);
  const send = async () => {
    setState({ sending: true });
    try {
      const r = await apiClient.post(`/leagues/${leagueId}/payment-reminders`);
      setState({ ok: true, text: r.data?.message || 'Reminder sent' });
    } catch (err) {
      setState({ ok: false, text: err.response?.data?.message || 'Could not send the reminder' });
    }
  };
  return (
    <div style={{ margin: '0 0 0.5rem' }}>
      <button
        type="button"
        data-testid="remind-unpaid"
        onClick={send}
        disabled={state?.sending}
        style={{ padding: '0.25rem 0.7rem', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.8rem', background: 'rgba(252,211,77,0.15)', border: '1px solid #fcd34d', color: '#fcd34d' }}
      >
        {state?.sending ? 'Sending…' : '🔔 Remind players who haven\'t paid the next night'}
      </button>
      {state?.text && <p role={state.ok ? 'status' : 'alert'} style={{ fontSize: '0.8rem', margin: '0.3rem 0 0', color: state.ok ? '#86efac' : '#fca5a5' }}>{state.text}</p>}
    </div>
  );
}

function LeagueFeesCard({ league }) {
  const [info, setInfo] = useState(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentReturn = searchParams.get('payment');
  const paymentId = searchParams.get('id');

  const load = useCallback(() => {
    apiClient.get(`/leagues/${league.id}/payments`)
      .then((r) => setInfo(r.data?.data || null))
      .catch(() => setInfo(null));
  }, [league.id]);

  useEffect(() => { load(); }, [load]);

  // Back from Stripe: cancelled → close it now; paid → the webhook can lag, look again
  useEffect(() => {
    if (!paymentReturn) return undefined;
    let timer;
    setSearchParams((prev) => { prev.delete('payment'); prev.delete('id'); return prev; }, { replace: true });
    if (paymentReturn === 'cancelled' && paymentId) {
      paymentService.cancelPayment(paymentId).catch(() => {}).finally(load);
      setNote('Payment cancelled: nothing was charged.');
    } else if (paymentReturn === 'success') {
      setNote('💳 Payment received. Thank you!');
      timer = setTimeout(load, 3000);
    }
    return () => clearTimeout(timer);
  }, [paymentReturn, paymentId, setSearchParams, load]);

  if (!info || info.fee_mode === 'none') return null;

  const price = formatMoney(info.fee_cents);
  const open = !['cancelled', 'completed'].includes(league.status);

  const pay = async (nightDate) => {
    setPaying(nightDate || 'season');
    setError('');
    try {
      const r = await apiClient.post(`/leagues/${league.id}/pay`, nightDate ? { night: nightDate } : {});
      const url = r.data?.data?.checkoutUrl;
      if (url) {
        window.location.assign(url);
        return;
      }
      if (r.data?.data?.status === 'paid') {
        // An earlier payment (removed, then entered again) already covers it
        setNote(`✅ ${r.data.data.message || 'Paid'}`);
        load();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not start the payment.');
    }
    setPaying(null);
  };

  const me = info.me;
  const canPay = info.enabled && open;

  return (
    <div data-testid="league-fees">
      <h3 className="form-section-title" style={{ marginBottom: '0.8rem' }}>💵 League fee</h3>
      <p style={{ color: '#e2e8f0', margin: '0 0 0.6rem' }}>
        {info.fee_mode === 'team_season' ? `${price} per team for the season` : `${price} per player per game night`}
        {!info.enabled && <span style={{ color: '#94a3b8' }}> (paid to the host, not in the app yet)</span>}
      </p>
      <RefundPolicyNote policy={league.refund_policy} />
      {note && <p role="status" style={{ color: '#86efac', margin: '0 0 0.6rem' }}>{note}</p>}
      {error && <p role="alert" style={{ color: '#fca5a5', margin: '0 0 0.6rem' }}>{error}</p>}

      {info.fee_mode === 'team_season' && me?.entry_id && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
          <span style={{ color: statusColor(me.payment_status) }}>Your season fee: {STATUS_TEXT[me.payment_status] || 'Unpaid'}</span>
          {canPay && me.payment_status !== 'paid' && (
            <button type="button" className="floating-btn create-btn-gaming" onClick={() => pay()} disabled={Boolean(paying)}>
              {paying ? 'Opening payment…' : `Pay ${price}`}
            </button>
          )}
        </div>
      )}

      {info.fee_mode === 'player_night' && me?.playing && (
        me.nights.length === 0 ? (
          <p style={{ color: '#94a3b8', margin: 0 }}>No upcoming game nights yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {me.nights.slice(0, 6).map((n) => (
              <li key={n.night} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.3rem 0' }}>
                <span style={{ color: '#e2e8f0', minWidth: '7rem' }}>{night(n.night)}</span>
                <span style={{ color: statusColor(n.payment_status) }}>{STATUS_TEXT[n.payment_status]}</span>
                {canPay && n.payment_status !== 'paid' && (
                  <button type="button" className="floating-btn create-btn-gaming" onClick={() => pay(n.night)} disabled={Boolean(paying)}>
                    {paying === n.night ? 'Opening…' : `Pay ${price}`}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )
      )}

      {info.entries && (
        <div style={{ marginTop: '1rem' }}>
          <h4 style={{ color: '#cbd5e1', margin: '0 0 0.4rem' }}>Who has paid</h4>
          <PaidSummary
            statuses={info.entries.map((e) => e.payment_status)}
            remindUrl={canPay ? `/leagues/${league.id}/payment-reminders` : null}
            note="Unpaid entries stay in until you remove them. Reminders go out at most once every 12 hours."
          />
          {info.enabled && <HostPaymentsLink />}
          {info.entries.length === 0 ? <p style={{ color: '#94a3b8', margin: 0 }}>No entries yet.</p> : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {info.entries.map((e) => (
                <li key={e.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}>
                  <span style={{ color: '#e2e8f0' }}>{e.name}</span>
                  <span style={{ color: statusColor(e.payment_status) }}>{STATUS_TEXT[e.payment_status] || 'Unpaid'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {info.nights && (
        <div style={{ marginTop: '1rem' }}>
          <h4 style={{ color: '#cbd5e1', margin: '0 0 0.4rem' }}>Paid by night</h4>
          {canPay && <RemindNextNight leagueId={league.id} />}
          {info.nights.length === 0 ? <p style={{ color: '#94a3b8', margin: 0 }}>Nobody has paid yet.</p> : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {info.nights.map((n) => (
                <li key={n.night} style={{ padding: '0.2rem 0', color: '#e2e8f0' }}>
                  {night(n.night)}: {n.players.filter((p) => p.payment_status === 'paid').map((p) => `@${p.username}`).join(', ') || 'nobody'}
                  {' '}({n.players.filter((p) => p.payment_status === 'paid').length})
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default LeagueFeesCard;
